// Feasibility API contracts (M7, WP7.1): the planning board's view of the simulator
// (@enp/domain `simulateFeasibility`, plan §5.6). Wire format as elsewhere: snake_case,
// quantities as decimal strings, instants ISO-8601 UTC, days `YYYY-MM-DD`. The simulator's
// camelCase results become these shapes through `feasibilityToWire` (the same shapes are
// stored with each run and synced to stations: `feasibility_batch_status.flags`).
//
//   GET  /feasibility/board               the branch's current run: coverage, batches, lines
//   GET  /feasibility/batches/:id         one batch's verdict and flags
//   GET  /feasibility/orders/:id          one order's line coverage
//   POST /feasibility/recompute           recompute a branch now
//
// A read whose branch changed since its run recomputes first (milliseconds), so answers are
// never stale; the worker also recomputes after every change, for the stations.
// Permissions: reads and recompute need the planning area (`production.plan` or
// `reports.view`), within the caller's branch scope.
import { z } from 'zod';
import { FEASIBILITY_FLAG_CODES } from '../feasibility/types.js';
import type {
  FeasibilityBatchResult,
  FeasibilityCoverage,
  FeasibilityFlag,
  FeasibilityLineResult,
} from '../feasibility/types.js';
import { qtyString } from '../quantity/index.js';
import { isPlainDate } from '../time/index.js';
import { stockProductRefSchema } from './stock.js';
import { branchRefSchema } from './stock-control.js';

const instant = z.iso.datetime({ offset: true });
const plainDate = z.string().refine(isPlainDate, 'Expected a date, YYYY-MM-DD');
const id = z.string().min(1);
const count = z.int().min(0);

/** Why a batch can't cover an order line (D-074); also the allocation refusals' codes. */
export const coverMismatchSchema = z.enum([
  'product_mismatch',
  'not_planned_for_line',
  'completes_after_promise',
  'not_fresh_at_promise',
]);

/** The flag codes of §5.6 step 3. */
export const feasibilityFlagCodeSchema = z.enum(FEASIBILITY_FLAG_CODES);

const component = z.object({ id, name: z.string() });

/** A feasibility flag: what, which component/lot/qty/day, and the words to show. */
export const feasibilityFlagSchema = z.discriminatedUnion('code', [
  z.object({
    code: z.literal('expires_before_use'),
    message: z.string(),
    data: z.object({
      component,
      day: plainDate,
      needed: qtyString,
      missing: qtyString,
      uom: z.string(),
      lots: z.array(
        z.object({
          /** `null` for stock without a lot, or a delivery / batch output still to come. */
          lot_id: id.nullable(),
          lot_code: z.string().nullable(),
          location_id: id,
          best_before: plainDate.nullable(),
          qty: qtyString,
        }),
      ),
    }),
  }),
  z.object({
    code: z.literal('shortfall'),
    message: z.string(),
    data: z.object({
      component,
      day: plainDate,
      needed: qtyString,
      available: qtyString,
      missing: qtyString,
      uom: z.string(),
      reserved_for_orders: qtyString,
      earlier_use: z.array(z.object({ batch_id: id, number: z.string(), qty: qtyString })),
      planned_batches: z.array(z.object({ batch_id: id, number: z.string(), day: plainDate })),
    }),
  }),
  z.object({
    code: z.literal('depends_on_unconfirmed_receipt'),
    message: z.string(),
    data: z.object({
      component,
      day: plainDate,
      qty: qtyString,
      uom: z.string(),
      receipts: z.array(
        z.object({
          receipt_id: id,
          po_id: id,
          po_number: z.string(),
          expected_date: plainDate,
          qty: qtyString,
        }),
      ),
    }),
  }),
  z.object({
    code: z.literal('intermediate_not_planned'),
    message: z.string(),
    data: z.object({
      component,
      day: plainDate,
      needed: qtyString,
      available: qtyString,
      missing: qtyString,
      uom: z.string(),
    }),
  }),
  z.object({
    code: z.literal('missing_data'),
    message: z.string(),
    data: z.object({
      reason: z.enum([
        'no_recipe',
        'missing_conversion',
        'unknown_product',
        'unknown_component',
        'no_shelf_life',
        'made_to_order_without_recipe',
        'recipe_loop',
      ]),
      product: z.object({ id, name: z.string().nullable() }),
      from: z.string().optional(),
      to: z.string().optional(),
    }),
  }),
]);
/** A feasibility flag on the wire. */
export type FeasibilityFlagWire = z.infer<typeof feasibilityFlagSchema>;

/**
 * Why a batch or line is at risk or short (D-074, UI7A-2): the flag at the root, on the batch
 * that carries it (with what that batch is expected to make), or a delivery a line counts on.
 */
export const feasibilityCauseSchema = z.object({
  batch_id: id.nullable(),
  number: z.string().nullable(),
  product_id: id.nullable(),
  verdict: z.enum(['feasible', 'at_risk', 'infeasible', 'not_evaluated']).nullable(),
  expected_yield: qtyString.nullable(),
  planned_qty: qtyString.nullable(),
  flag: feasibilityFlagSchema.nullable(),
  receipt: z
    .object({
      receipt_id: id,
      po_id: id,
      po_number: z.string(),
      expected_date: plainDate,
      qty: qtyString,
    })
    .nullable(),
  message: z.string(),
});
/** A cause on the wire. */
export type FeasibilityCauseWire = z.infer<typeof feasibilityCauseSchema>;

/** One batch's verdict. */
export const feasibilityBatchSchema = z.object({
  batch_id: id,
  number: z.string(),
  product_id: id,
  status: z.enum(['planned', 'started']),
  planned_start: instant,
  day: plainDate,
  planned_qty: qtyString,
  expected_yield: qtyString,
  verdict: z.enum(['feasible', 'at_risk', 'infeasible', 'not_evaluated']),
  evaluated: z.boolean(),
  not_evaluated_reasons: z.array(z.string()),
  flags: z.array(feasibilityFlagSchema),
  components: z.array(
    z.object({
      product_id: id,
      needed: qtyString,
      allocated: qtyString,
      uom: z.string(),
      from_receipts: qtyString,
      from_batches: qtyString,
    }),
  ),
  depends_on: z.array(z.object({ batch_id: id, number: z.string() })),
  /** What is wrong upstream, traced to the root flag (runs before engine 2 carry none). */
  causes: z.array(feasibilityCauseSchema).default([]),
});
/** One batch's verdict on the wire. */
export type FeasibilityBatchWire = z.infer<typeof feasibilityBatchSchema>;

/** One order line's coverage. */
export const feasibilityLineSchema = z.object({
  order_line_id: id,
  order_id: id,
  order_number: z.string(),
  position: z.int().min(1),
  product_id: id.nullable(),
  description: z.string().nullable(),
  qty: qtyString,
  promised_at: instant.nullable(),
  day: plainDate.nullable(),
  status: z.enum(['covered_by_stock', 'covered_by_batch', 'uncovered', 'not_evaluated']),
  evaluated: z.boolean(),
  covered_qty: qtyString,
  missing_qty: qtyString,
  sources: z.array(
    z.object({
      kind: z.enum(['claim', 'stock', 'batch', 'receipt']),
      qty: qtyString,
      batch_id: id.optional(),
      batch_number: z.string().optional(),
      lot_id: id.optional(),
      lot_code: z.string().nullable().optional(),
      receipt_id: id.optional(),
      po_number: z.string().optional(),
    }),
  ),
  batch_numbers: z.array(z.string()),
  at_risk: z.boolean(),
  /** Why it is at risk or short, traced to the root flag (runs before engine 2 carry none). */
  causes: z.array(feasibilityCauseSchema).default([]),
  /**
   * Batches allocated to it that can't cover it (D-074): another product, not planned for it,
   * not made by the promise, or past its best-before by the promised day.
   */
  ignored_batches: z
    .array(
      z.object({
        batch_id: id,
        number: z.string(),
        product_id: id,
        why: coverMismatchSchema.default('product_mismatch'),
        /** The trading day it is made (runs before engine 3 carry none). */
        day: plainDate.nullable().default(null),
        best_before: plainDate.nullable().default(null),
      }),
    )
    .default([]),
  reason: z
    .enum([
      'short_yield',
      'batch_after_promise',
      'claim_expires',
      'custom_item_unplanned',
      'not_enough_stock',
      'no_promised_time',
      'unknown_product',
      'made_to_order_product',
      'depends_on_unevaluated_batch',
    ])
    .nullable(),
  message: z.string(),
});
/** One order line's coverage on the wire. */
export type FeasibilityLineWire = z.infer<typeof feasibilityLineSchema>;

/** The coverage report (lesson 12): what was evaluated, what could not be, and why. */
export const feasibilityCoverageSchema = z.object({
  status: z.enum(['all_clear', 'issues', 'incomplete', 'nothing_planned']),
  complete: z.boolean(),
  horizon: z.object({ from: plainDate, to: plainDate, days: z.int().min(1) }),
  batches: z.object({
    total: count,
    evaluated: count,
    feasible: count,
    at_risk: count,
    infeasible: count,
    not_evaluated: z.array(
      z.object({ batch_id: id, number: z.string(), reasons: z.array(z.string()) }),
    ),
    beyond_horizon: count,
  }),
  lines: z.object({
    total: count,
    evaluated: count,
    covered: count,
    uncovered: count,
    /** Of the uncovered: lines partly covered (runs before engine 3 carry none). */
    partly_covered: count.default(0),
    at_risk: count,
    not_evaluated: z.array(
      z.object({
        order_line_id: id,
        order_number: z.string(),
        position: z.int().min(1),
        reason: z.enum([
          'no_promised_time',
          'unknown_product',
          'made_to_order_product',
          'depends_on_unevaluated_batch',
        ]),
      }),
    ),
    beyond_horizon: count,
  }),
  receipts: z.object({
    total: count,
    simulated: count,
    no_expected_date: count,
    beyond_horizon: count,
  }),
  summary: z.string(),
});
/** The coverage report on the wire. */
export type FeasibilityCoverageWire = z.infer<typeof feasibilityCoverageSchema>;

const snake = (key: string) => key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

/** A camelCase structure with every object key in snake_case (values are left alone). */
function snakeKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(snakeKeys);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [snake(k), snakeKeys(v)]),
    );
  }
  return value;
}

/** The simulator's flags, batches, lines and coverage in their wire shapes. */
export const feasibilityToWire = {
  flag: (f: FeasibilityFlag) => snakeKeys(f) as FeasibilityFlagWire,
  batch: (b: FeasibilityBatchResult) => snakeKeys(b) as FeasibilityBatchWire,
  line: (l: FeasibilityLineResult) => snakeKeys(l) as FeasibilityLineWire,
  coverage: (c: FeasibilityCoverage) => snakeKeys(c) as FeasibilityCoverageWire,
};

// ---------------------------------------------------------------------------------------
// Endpoints

/** Which run answered, and how fresh it is. */
export const feasibilityRunMetaSchema = z.object({
  id: z.uuid(),
  branch: branchRefSchema,
  /** When the run's result was last confirmed (a recompute with unchanged inputs moves it). */
  computed_at: instant,
  /** When this result was first computed. */
  run_at: instant,
  business_day: plainDate,
  horizon_days: z.int().min(1),
  status: z.enum(['all_clear', 'issues', 'incomplete', 'nothing_planned']),
  complete: z.boolean(),
  summary: z.string(),
  inputs_hash: z.string(),
  engine_version: z.int().min(1),
  trigger: z.enum(['change', 'read', 'manual', 'sweep']),
  /** Something changed after this run (only while a recompute is on its way). */
  stale: z.boolean(),
});
/** Run metadata. */
export type FeasibilityRunMeta = z.infer<typeof feasibilityRunMetaSchema>;

/** `GET /feasibility/board` query: a branch (default: the caller's or the default branch), a day range. */
export const feasibilityBoardQuerySchema = z.object({
  branch_id: z.uuid().optional(),
  /** First day shown (default: the run's business day). */
  from: plainDate.optional(),
  /** Last day shown (default: the end of the horizon). */
  to: plainDate.optional(),
});

/** `GET /feasibility/board` response. */
export const feasibilityBoardSchema = z.object({
  run: feasibilityRunMetaSchema,
  coverage: feasibilityCoverageSchema,
  /** Batches judged on a day in the range, by day and start. */
  batches: z.array(feasibilityBatchSchema),
  /** Lines handed over on a day in the range (and lines without a promise), by day. */
  lines: z.array(feasibilityLineSchema),
  /** Every product the batches and lines name. */
  products: z.array(stockProductRefSchema),
  /**
   * What the board shows beside each batch (WP7.2): its pinned recipe version, station,
   * origin and note (the run itself carries only what the simulator needs).
   */
  batch_details: z.array(
    z.object({
      batch_id: z.uuid(),
      recipe_version: z.object({ id: z.uuid(), recipe_id: z.uuid(), version_no: z.int().min(1) }),
      station: z.object({ id: z.uuid(), name: z.string() }).nullable(),
      origin: z.enum(['admin', 'station']),
      note: z.string().nullable(),
      /**
       * Its output's best-before if made as planned (D-074: a batch covers a line only if it is
       * still in date on the promised day); `null` when the product keeps none.
       */
      best_before: plainDate.nullable().default(null),
      /** The order line it is planned for (D-074: the only batch that can cover a custom item). */
      for_order_line: z
        .object({
          id: z.uuid(),
          order_id: z.uuid(),
          order_number: z.string(),
          position: z.int().min(1),
        })
        .nullable()
        .default(null),
    }),
  ),
  /**
   * The batches' live planned allocations (WP7.2): who each batch's output is promised to.
   * The shelf's share is "the rest" of the batch.
   */
  allocations: z.array(
    z.object({
      id: z.uuid(),
      batch_id: z.uuid(),
      destination: z.enum(['shelf', 'order_claim', 'branch_transfer', 'immediate_order']),
      qty: qtyString,
      order_line_id: z.uuid().nullable(),
      order: z.object({ id: z.uuid(), number: z.string(), position: z.int().min(1) }).nullable(),
    }),
  ),
});
/** The planning board. */
export type FeasibilityBoard = z.infer<typeof feasibilityBoardSchema>;

/** `:id` path parameter. */
export const feasibilityIdParamsSchema = z.object({ id: z.uuid() });

/** `GET /feasibility/batches/:id` response. */
export const feasibilityBatchResponseSchema = z.object({
  run: feasibilityRunMetaSchema,
  /** `null` when the batch is not in the run: finished, cancelled, or after the horizon. */
  batch: feasibilityBatchSchema.nullable(),
  products: z.array(stockProductRefSchema),
});

/** `GET /feasibility/orders/:id` response. */
export const feasibilityOrderResponseSchema = z.object({
  /** `null` when the order has no branch (nothing to simulate it in). */
  run: feasibilityRunMetaSchema.nullable(),
  /** The order's lines in the run (none when it is not confirmed or after the horizon). */
  lines: z.array(feasibilityLineSchema),
  products: z.array(stockProductRefSchema),
});

/** `POST /feasibility/recompute` body. */
export const feasibilityRecomputeRequestSchema = z.object({ branch_id: z.uuid().optional() });

/** `POST /feasibility/recompute` response. */
export const feasibilityRecomputeResponseSchema = z.object({
  run: feasibilityRunMetaSchema,
  coverage: feasibilityCoverageSchema,
  /** A new run was stored (`false`: the inputs had not changed). */
  stored: z.boolean(),
});
